// AGC: Changelog Parser Utility
// AGC: Parses CHANGELOG_hub.md and changelog_origami.md, filters out PlayerAI/TournamentAI entries

export interface ChangelogEntry {
  version: string
  date: string
  title: string
  source: 'hub' | 'origami'
  content: string
  fullText: string
}

/**
 * AGC: Parse a changelog markdown file and extract version entries
 */
export function parseChangelog(content: string, source: 'hub' | 'origami'): ChangelogEntry[] {
  const entries: ChangelogEntry[] = []
  const lines = content.split('\n')
  
  let currentEntry: Partial<ChangelogEntry> | null = null
  let currentContent: string[] = []
  let inEntry = false
  
  // AGC: Debug logging
  console.log(`AGC: Parsing ${source} changelog, ${lines.length} lines`)
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    
    // AGC: Match version header: ## 🚀 Version X.X.X - Title (YYYY-MM-DD)
    // AGC: Updated regex to handle titles with special characters
    const versionMatch = line.match(/^## 🚀 Version ([\d.]+) - (.+?) \((\d{4}-\d{2}-\d{2})\)/)
    
    if (versionMatch) {
      console.log(`AGC: Found version header: ${versionMatch[1]} - ${versionMatch[2]} (${versionMatch[3]})`)
      // AGC: Save previous entry if exists
      if (currentEntry && inEntry) {
        const fullText = currentContent.join('\n')
        // AGC: Filter out PlayerAI and TournamentAI entries for origami
        if (source === 'origami') {
          const lowerText = fullText.toLowerCase()
          if (lowerText.includes('playerai') || lowerText.includes('tournamentai')) {
            // AGC: Skip this entry
            currentEntry = null
            currentContent = []
            inEntry = false
            continue
          }
        }
        
        entries.push({
          version: currentEntry.version!,
          date: currentEntry.date!,
          title: currentEntry.title!,
          source: source,
          content: currentContent.slice(0, 10).join('\n'), // AGC: First 10 lines for preview
          fullText: fullText,
        })
      }
      
      // AGC: Start new entry
      currentEntry = {
        version: versionMatch[1],
        date: versionMatch[3],
        title: versionMatch[2],
      }
      currentContent = [line]
      inEntry = true
    } else if (inEntry && currentEntry) {
      // AGC: Check if we hit the next version or end of entry
      if (line.match(/^## 🚀 Version/) || line.match(/^---$/)) {
        // AGC: End of current entry
        const fullText = currentContent.join('\n')
        if (source === 'origami') {
          const lowerText = fullText.toLowerCase()
          if (lowerText.includes('playerai') || lowerText.includes('tournamentai')) {
            currentEntry = null
            currentContent = []
            inEntry = false
            continue
          }
        }
        
        entries.push({
          version: currentEntry.version!,
          date: currentEntry.date!,
          title: currentEntry.title!,
          source: source,
          content: currentContent.slice(0, 10).join('\n'),
          fullText: fullText,
        })
        
        currentEntry = null
        currentContent = []
        inEntry = false
        
        // AGC: If this is a new version, start it
        const nextVersionMatch = line.match(/^## 🚀 Version ([\d.]+) - (.+?) \((\d{4}-\d{2}-\d{2})\)/)
        if (nextVersionMatch) {
          currentEntry = {
            version: nextVersionMatch[1],
            date: nextVersionMatch[3],
            title: nextVersionMatch[2],
          }
          currentContent = [line]
          inEntry = true
        }
      } else {
        currentContent.push(line)
      }
    }
  }
  
  // AGC: Save last entry if exists
  if (currentEntry && inEntry) {
    const fullText = currentContent.join('\n')
    if (source === 'origami') {
      const lowerText = fullText.toLowerCase()
      if (!lowerText.includes('playerai') && !lowerText.includes('tournamentai')) {
        entries.push({
          version: currentEntry.version!,
          date: currentEntry.date!,
          title: currentEntry.title!,
          source: source,
          content: currentContent.slice(0, 10).join('\n'),
          fullText: fullText,
        })
      }
    } else {
      entries.push({
        version: currentEntry.version!,
        date: currentEntry.date!,
        title: currentEntry.title!,
        source: source,
        content: currentContent.slice(0, 10).join('\n'),
        fullText: fullText,
      })
    }
  }
  
  console.log(`AGC: Parsed ${entries.length} entries from ${source} changelog`)
  return entries
}

/**
 * AGC: Sort entries by date (newest first)
 */
export function sortEntriesByDate(entries: ChangelogEntry[]): ChangelogEntry[] {
  return entries.sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    // AGC: If dates are equal, sort by version number (newest first)
    if (dateA === dateB) {
      return compareVersions(b.version, a.version)
    }
    return dateB - dateA // AGC: Newest first
  })
}

/**
 * AGC: Compare version strings (e.g., "1.1.0" vs "1.0.0")
 */
function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number)
  const parts2 = v2.split('.').map(Number)
  const maxLength = Math.max(parts1.length, parts2.length)
  
  for (let i = 0; i < maxLength; i++) {
    const part1 = parts1[i] || 0
    const part2 = parts2[i] || 0
    if (part1 > part2) return 1
    if (part1 < part2) return -1
  }
  return 0
}

